/**
 * Copyright (c) 2023, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { NavigateNext } from "@mui/icons-material";
import { Box, Container, Link, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { useCloud } from "../Hooks/Cloud";
import { useHeader } from "../Hooks/Header";

/**
 * Not found page
 * Description: This component is used to display the 404 page
 */
function NotFound({ isHeaderVisible, isCurrentStatusVisible }) {
  const { cloud } = useCloud();
  const { setIsVisible } = useHeader();

  useEffect(() => {
    document.title = window.ENV[cloud].title + " - 404";
    setIsVisible(isHeaderVisible);
  }, []);

  return (
    <>
      <Container
        maxWidth="md"
        sx={ {
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          minHeight: "60vh"
        } }
      >
        <Container>
          <Typography variant="h4" component={ "div" } sx={ { my: 2 } }>
            404
          </Typography>
          <Typography variant="h6" component={ "div" } sx={ { my: 2 } }>
            Page not found
          </Typography>
        </Container>
      </Container>
      { isCurrentStatusVisible && (
        <Container maxWidth="md" sx={ { my: 4 } }>
          <Container>
            <Box
              sx={ {
                alignItems: "center",
                display: "flex",
                justifyContent: "flex-end"
              } }
            >
              <Link href={ "/" } variant="body2" underline="none">
                <Typography component={ "span" } variant="inherit">
                  <Box component="span" sx={ { alignItems: "center", display: "flex" } }>
                    <Box component="span" sx={ { pr: 1 } }>
                      Current Status
                    </Box>
                    <NavigateNext />
                  </Box>
                </Typography>
              </Link>
            </Box>
          </Container>
        </Container>
      ) }
    </>
  );
}

NotFound.propTypes = {
  isCurrentStatusVisible: PropTypes.bool,
  isHeaderVisible: PropTypes.bool
};

export default NotFound;
